<!-- Full-height page: :host is 100% of the layout's content area; the flex
     column chain (flex-1 + min-h-0 at each level) hands the remaining height
     down to the list, which scrolls internally. -->
<section class="p-6 h-full flex flex-col">
  <div class="bg-white rounded-xl flex-1 min-h-0 flex flex-col">

    <!-- Existing Filters Display -->
    <div class="shrink-0 flex flex-row justify-between items-start w-full">
      <div class="-m-1 flex flex-wrap items-center mb-0 w-full">
        <ng-container *ngIf="searchFilters$ | async as filters">
          <ng-container *ngIf="filterTypes$ | async as types">
                <span *ngFor="let filter of getSelectedFilters(filters)" class="m-1 inline-flex items-center rounded-full bg-gray-50 py-1.5 px-3 text-xs font-medium text-gray-500">
                  <span [ngClass]="{'text-secondary': isPersonFilter(filter.filterType, types)}">
                    {{ getFilterLabel(filter.filterType, types) }}: {{ getFilterValue(filter, types) }}
                  </span>
                </span>
          </ng-container>
        </ng-container>

        <a routerLink="/advanced-search" class="ml-1 bg-blue-royal p-1.5 rounded-full" matTooltip="Edit Filters" matTooltipPosition="above" matTooltipClass="custom-tooltip">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-3 text-white">
            <path d="m2.695 14.762-1.262 3.155a.5.5 0 0 0 .65.65l3.155-1.262a4 4 0 0 0 1.343-.886L17.5 5.501a2.121 2.121 0 0 0-3-3L3.58 13.419a4 4 0 0 0-.885 1.343Z" />
          </svg>
        </a>
      </div>

      <div class="text-sm font-poppins font-normal text-secondary whitespace-nowrap">
        <span class="font-bold font-poppins">{{ (totalPeople$ | async) | number:'1.0-0' }}</span> results found
      </div>
    </div>

    <div class="shrink-0 lg:flex lg:flex-row lg:justify-between items-center mx-auto w-full max-w-7xl px-0 sm:px-6 lg:px-8 py-8">

      <div class="flex flex-col w-full space-y-3">

        <!-- Company Display - Full Width Row -->
        <ng-container *ngIf="selectedCompany$ | async as company">
          <div class="grid grid-cols-3 gap-3">

            <div class="flex items-center space-x-3 bg-gray-50 p-3 rounded-xl w-full h-full">
              <img class="h-10 w-10 md:h-12 md:w-12 flex-none rounded-md bg-gray-50"
                   [src]="'https://ui-avatars.com/api/?background=FFA500&color=fff&rounded=false&name=' + (company.company_name?.substring(0, 2) || 'NA') + '&format=svg&size=128'"
                   [alt]="company.company_name"
              />
              <div class="flex flex-col">
                <p class="font-poppins font-bold text-gray-700">{{ company.company_name }}</p>
                <p class="text-gray-500 text-xs font-normal font-poppins">{{ getLocation(company.main_address) }}</p>
              </div>
            </div>

            <div class="col-span-2 flex justify-between items-center bg-gray-50 p-3 rounded-xl h-full w-full">

              <div class="flex justify-center items-center space-x-3 text-xs text-gray-400 font-normal font-poppins w-full">
                <span *ngIf="company.year_founded" class="font-poppins">Founded {{ company.year_founded }}</span>
                <span *ngIf="company.year_founded && (company.num_locations || company.employee_count?.value || company.revenue?.value)" class="text-gray-300">•</span>
                <span *ngIf="company.num_locations" class="font-poppins">{{ company.num_locations }} {{ company.num_locations === 1 ? 'Location' : 'Locations' }}</span>
                <span *ngIf="company.num_locations && (company.employee_count?.value || company.revenue?.value)" class="text-gray-300">•</span>
                <span *ngIf="company.employee_count?.value" class="font-poppins">{{ formatEmployeeCount(company.employee_count.value) }} Employees</span>
                <span *ngIf="company.employee_count?.value && company.revenue?.value" class="text-gray-300">•</span>
                <span *ngIf="company.revenue?.value" class="font-poppins">${{ formatRevenue(company.revenue.value) }} Revenue</span>
              </div>

              <div class="flex items-center space-x-3">

                <a *ngIf="company.website_url" [href]="company.website_url" target="_blank" class="bg-gray-100 p-2 rounded-lg hover:opacity-90" matTooltip="Website" matTooltipPosition="above" matTooltipClass="custom-tooltip">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-5 text-primary">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418" />
                  </svg>
                </a>

                <button (click)="openCompanyDetails(company)" class="bg-gray-100 p-2 rounded-lg hover:opacity-90" matTooltip="Information" matTooltipPosition="above" matTooltipClass="custom-tooltip">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-5 text-primary">
                    <path fill-rule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9Z" clip-rule="evenodd" />
                  </svg>
                </button>

                <a *ngIf="company.linkedin_url" [href]="company.linkedin_url" target="_blank" rel="noopener noreferrer" class="bg-gray-100 p-2 rounded-lg hover:opacity-90" matTooltip="LinkedIn" matTooltipPosition="above" matTooltipClass="custom-tooltip">
                  <svg class="size-5 text-primary" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19a.66.66 0 000 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z"></path>
                  </svg>
                </a>

              </div>

            </div>

          </div>
        </ng-container>

        <!-- Person Filters Search Section - Single Row -->
        <div>
          <div class="grid grid-cols-3 gap-3">

            <!-- Person Titles Input -->
            <div class="col-span-2 flex-1">
              <app-filter-tags
                [control]="personTitlesControl"
                [required]="false"
                [showlabel]="false"
                placeholder="Enter Job Titles (press Enter or comma to add)"
              ></app-filter-tags>
            </div>

            <div class="flex items-start space-x-3">

              <!-- Person Seniorities Dropdown -->
              <div class="flex-1">
                <app-filter-combobox-multiselect
                  [label]="'Person Seniorities'"
                  [options]="seniorityOptions"
                  [required]="false"
                  [placeholder]="'Select Seniority Levels'"
                  [formControl]="personSenioritiesControl"
                  class="w-full"
                ></app-filter-combobox-multiselect>
              </div>

              <!-- Search and Clear Buttons -->
              <button
                (click)="onPersonFiltersSearch()"
                [disabled]="loading$ | async"
                class="ml-1 text-sm text-white font-medium bg-blue-royal py-2 rounded-md hover:opacity-90 px-6"
              >
                <span *ngIf="!(loading$ | async)">Search</span>
                <span *ngIf="(loading$ | async)">Searching...</span>
              </button>

              <button
                (click)="clearPersonFilters()"
                [disabled]="personTitles.length === 0 && personSeniorities.length === 0"
                class="hover:bg-red-100 p-2 rounded-full cursor-pointer"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-5 text-red-500">
                  <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
                </svg>
              </button>

            </div>

          </div>
        </div>

      </div>
    </div>

    <div class="w-full flex flex-col flex-1 min-h-0 mx-auto max-w-7xl px-0 sm:px-6 lg:px-8 mt-3">

      <!-- Loading state - Show when initially loading OR store loading is true -->
      <ng-container *ngIf="isInitialLoading || (loading$ | async)">
        <!-- Skeleton fills the same full-height box as the real list; extra
             rows are clipped (overflow-hidden) so it never shows a scrollbar
             and looks "full" at any viewport height. -->
        <ul class="border border-gray-100 rounded-xl px-3 divide-y divide-gray-100 flex-1 min-h-0 overflow-hidden">
          <li class="flex justify-between gap-x-3 py-3" *ngFor="let _ of skeletonRows">
            <div class="flex flex-row justify-between items-center space-x-3 space-y-3 w-full">
              <div class="flex flex-row items-center space-x-2 rounded-lg w-full animate-pulse">
                <div class="h-10 w-10 md:h-12 md:w-12 flex-none rounded-full bg-gray-100"></div>
                <div class="flex flex-col space-y-2 flex-1">
                  <div class="h-4 w-32 bg-gray-100 rounded"></div>
                  <div class="h-3 w-48 bg-gray-100 rounded"></div>
                </div>
              </div>
              <div class="h-9 w-32 bg-gray-100 rounded animate-pulse !mt-0"></div>
            </div>
          </li>
        </ul>
      </ng-container>

      <!-- People list - only show when NOT initially loading AND NOT store loading -->
      <ng-container *ngIf="!isInitialLoading && !(loading$ | async)">
        <ng-container *ngIf="(people$ | async)?.length > 0; else noPeopleFound">
          <ul class="border border-gray-100 rounded-xl px-3 divide-y divide-gray-100 flex-1 min-h-0 overflow-y-auto overscroll-contain transition-all duration-400 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-200 hover:scrollbar-thumb-transparent">
            <li *ngFor="let person of people$ | async; let i = index" class="flex justify-between gap-x-3 py-3 w-full">
              <div class="flex flex-row justify-between items-center space-x-3 w-full">
                <div class="flex flex-row space-x-2 items-center rounded-lg w-2/5">
                  <img
                    class="h-6 w-6 md:h-12 md:w-12 flex-none rounded-full bg-gray-50"
                    [src]="(person.photo_url === '[]' || person.photo_url?.startsWith('https://static.licdn.com')) ? 'https://ui-avatars.com/api/?background=FFA500&color=fff&rounded=true&name=' + person.name + '&format=svg&size=128' : person.photo_url"
                    [alt]="person.name"
                  />
                  <div class="flex flex-col">
                    <p class="font-poppins text-sm font-bold text-gray-700">{{ person.name }}</p>
                    <p class="flex text-xs leading-5">
                      <span class="text-gray-500" *ngIf="person?.country">
                        {{ person?.country }} / {{ person?.title?.length > 50 ? (person?.title.slice(0, 50) + '...') : person?.title }}
                      </span>
                    </p>
                  </div>
                </div>
                <div class="flex flex-row items-center justify-center">
                  <button
                    *ngIf="!person.reportStatus"
                    class="ml-1 text-sm text-white font-normal bg-blue-royal px-3 py-2 rounded-md hover:opacity-90"
                    (click)="generateReport(person)"
                  >
                    Generate report
                  </button>
                  <div *ngIf="person.reportStatus === 'generating'">
                    <svg
                      class="mx-auto animate-spin h-6 w-6 text-primary mb-2"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                      ></circle>
                      <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <h1 class="text-sm font-poppins leading-none tracking-tight font-normal text-black">
                      Just a sec...
                    </h1>
                  </div>
                  <button
                    *ngIf="person.reportStatus === 'ready'"
                    class="ml-1 text-sm text-blue-royal px-4 py-2.5 underline"
                    (click)="goToReport(person)"
                  >
                    Open Report
                  </button>
                </div>
              </div>
            </li>
          </ul>
        </ng-container>

        <!-- No people found message -->
        <ng-template #noPeopleFound>
          <div class="flex flex-col flex-1 min-h-0 items-center justify-center py-8 border border-gray-100 rounded-xl px-3">
            <p class="text-gray-500 text-md mb-4">No people were found.</p>
            <a routerLink="/advanced-search/companies" class="btn btn-rounded flex items-center space-x-1 text-sm">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-4 text-blue-royal">
                <path fill-rule="evenodd" d="M17 10a.75.75 0 0 1-.75.75H5.612l4.158 3.96a.75.75 0 1 1-1.04 1.08l-5.5-5.25a.75.75 0 0 1 0-1.08l5.5-5.25a.75.75 0 1 1 1.04 1.08L5.612 9.25H16.25A.75.75 0 0 1 17 10Z" clip-rule="evenodd" />
              </svg>
              <span class="text-blue-royal !font-normal">Back to companies</span>
            </a>
          </div>
        </ng-template>
      </ng-container>

      <!-- Pagination - only show when we have data and not loading -->
      <app-advanced-search-pagination
        *ngIf="hasData && !isInitialLoading && !(loading$ | async) && (people$ | async)?.length > 0"
        [pagination]="pagination"
        (pageChanged)="onPageChanged($event)"
        (perPageChanged)="onPerPageChanged($event)"
        class="shrink-0 mt-3"
      ></app-advanced-search-pagination>

      <div class="shrink-0 w-full mt-6 flex justify-between flex-row mb-6">

        <div>
          <a routerLink="/advanced-search/companies" class="lg:relative lg:right-6 btn btn-rounded flex items-center space-x-1 text-sm">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-4 text-blue-royal">
              <path fill-rule="evenodd" d="M17 10a.75.75 0 0 1-.75.75H5.612l4.158 3.96a.75.75 0 1 1-1.04 1.08l-5.5-5.25a.75.75 0 0 1 0-1.08l5.5-5.25a.75.75 0 1 1 1.04 1.08L5.612 9.25H16.25A.75.75 0 0 1 17 10Z" clip-rule="evenodd" />
            </svg>
            <span class="text-blue-royal !font-normal">Back</span>
          </a>
        </div>

        <div>
          <a routerLink="/reports" class="lg:relative lg:left-6 btn btn-rounded flex items-center space-x-1 text-sm">
            <span class="text-blue-royal !font-normal">See my reports</span>
          </a>
        </div>

      </div>

    </div>

  </div>
</section>

<!-- Workspace Selection Modal -->
<app-workspace-selection-modal
  *ngIf="showWorkspaceModal"
  (reportGenerated)="onReportGenerated($event)"
  (modalClosed)="onModalClosed()">
</app-workspace-selection-modal>

<!-- Company Details Modal -->
<app-company-details-modal
  [isVisible]="isCompanyModalVisible"
  [company]="selectedCompanyForModal"
  (modalClosed)="closeCompanyModal()">
</app-company-details-modal>
